import { AppendOnlyList, IterableList } from './collections';
import { untilIdle } from './idlecallback';

export type AsyncProcessOptions = {
  /**
   * How long to process work items synchronously before switching to async
   * processing.
   */
  readonly maxSyncMs?: number;

  /**
   * How many work items we should process between yield checks. This can be
   * used to reduce overhead when work items are small, since checking if we
   * need to yield has a performance cost.
   */
  readonly workItemGroupSize?: number;
};

export class WorkQueueProcessor<WorkItem> {
  private _queue = new AppendOnlyList<WorkItem>();
  private _activeIterator: IterableIterator<Readonly<WorkItem>> | undefined;

  constructor(
    private readonly _name: string,
    private readonly _process: (item: WorkItem) => void,
  ) {}

  /** Append a list of work items to the queue. */
  addWork(work: IterableList<WorkItem>): void {
    this._queue.append(work);
    console.debug(`${this._name}: added ${work.length} work items.`);
  }

  /** Process un-processed items in the queue synchronously. */
  processSync(): void {
    this._processAsync(undefined, { maxSyncMs: Number.POSITIVE_INFINITY });
  }

  /**
   * Process un-processed items in the queue synchronously as long as a
   * condition is true.
   *
   * @param whileCondition - The condition to check. The condition is evaluated
   * after each item is processed, so we will always process at least one item.
   */
  processSyncWhile(whileCondition: (item: WorkItem) => boolean): void {
    this._processAsync(whileCondition, { maxSyncMs: Number.POSITIVE_INFINITY });
  }

  /**
   * Process un-processed items in the queue asynchronously.
   */
  async processAsync(options?: AsyncProcessOptions): Promise<void> {
    return this._processAsync(undefined, options);
  }

  /**
   * Process un-processed items in the queue asynchronously as long as a
   * condition is true.
   *
   * @param whileCondition - The condition to check. The condition is evaluated
   * after each item is processed, so we will always process at least one item.
   */
  async processAsyncWhile(
    whileCondition: (item: WorkItem) => boolean,
    options?: AsyncProcessOptions,
  ): Promise<void> {
    return this._processAsync(whileCondition, options);
  }

  private async _processAsync(
    whileCondition?: (item: WorkItem) => boolean,
    options?: AsyncProcessOptions,
  ): Promise<void> {
    let targetTaskDurationMs = options?.maxSyncMs ?? 16; // One frame at 60fps.
    const workItemGroupSize = options?.workItemGroupSize ?? 1;

    // If we're not already in the middle of iterating through the queue, create
    // a new iterator.
    if (!this._activeIterator) {
      this._activeIterator = this._queue[Symbol.iterator]();
    }

    // Process each item in the queue until we either process all the items,
    // fail the 'whileCondition', or exceed the maximum time allowed for this
    // task.
    let taskStartTimeMs = Date.now();
    let taskItemsProcessed = 0;
    let processedAnyItems = false;
    while (true) {
      if (!this._activeIterator) {
        break;
      }

      const next = this._activeIterator?.next();
      if (next.done) {
        break;
      }

      const item = next.value;
      this._process(item);
      taskItemsProcessed++;
      processedAnyItems = true;

      if (taskItemsProcessed % workItemGroupSize !== 0) {
        continue;
      }

      if (whileCondition?.(item) === false) {
        console.debug(
          `${this._name}: processed ${taskItemsProcessed} work items in ` +
            `${Date.now() - taskStartTimeMs}ms. Stopped due to condition.`,
        );
        return;
      }

      // If the target task duration is unbounded (which is another way of
      // saying we're processing the work queue synchronously, since we'll never
      // yield), skip the logic below, since there is a performance cost for
      // uselessly calling Date.now().
      if (!Number.isFinite(targetTaskDurationMs)) {
        continue;
      }

      const taskDurationSoFarMs = Date.now() - taskStartTimeMs;
      if (taskDurationSoFarMs >= targetTaskDurationMs) {
        console.debug(
          `${this._name}: processed ${taskItemsProcessed} work items in ` +
            `${taskDurationSoFarMs}ms. Yielding the thread.`,
        );

        // Resume processing in a new async task.
        const deadline = await untilIdle({ timeout: 32 });
        targetTaskDurationMs = deadline.timeRemaining();
        taskStartTimeMs = Date.now();
        taskItemsProcessed = 0;
      }
    }

    // If we reach this point, we've processed all work items.
    this._activeIterator = undefined;

    // If the queue was empty, leave it as-is and return silently.
    if (!processedAnyItems) {
      return;
    }

    this._queue = new AppendOnlyList<WorkItem>();
    console.debug(
      `${this._name}: processed ${taskItemsProcessed} work items in ` +
        `${Date.now() - taskStartTimeMs}ms. Finished processing the work queue.`,
    );
  }
}
